/*
 *
 *  * Copyright (c) 2020 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { SortCriteria } from '../../types/sort-criteria';
import { VirtualGridColumn } from '../../types/virtual-grid-column';
import { ContextMenuOperation } from '../../types/context-menu-operation';
import { ContextMenuService } from '@wfm/core';
import { FilterCriteria } from '../../filter';
import { DOCUMENT } from '@angular/common';
import { ChangeDetector } from '@wfm/core';

@Component({
  selector: 'virtual-grid-header',
  templateUrl: './virtual-grid-header.component.html',
  styleUrls: ['./virtual-grid-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualGridHeaderComponent extends ChangeDetector {
  @Input() layout: Array<VirtualGridColumn>;
  @Input() frozenColumn: VirtualGridColumn;
  @Output() frozenColumnChange = new EventEmitter<VirtualGridColumn>();
  @Input() frozenColumns: Array<VirtualGridColumn>;
  @Input() unfrozenColumns: Array<VirtualGridColumn>;
  @Input() frozenSize = 0;
  @Input() locale?: string;
  @Input() gridWidth = 0;
  @Input() hidden = false;
  @Input() disableHeaderContextMenu = false;

  @Input() checkboxSelection = false;

  @Input() accessibleInstitutes: { id: string }[] = [];
  @Input() instituteSeparationEnabled = false;

  private $sortCriteria: SortCriteria[] = [];
  @Input()
  set sortCriteria(value: SortCriteria[]) {
    this.$sortCriteria = [...(value || [])];
  }
  get sortCriteria(): SortCriteria[] {
    return this.$sortCriteria;
  }

  @Input() selectAll = false;
  @Input() horizontalScrollPosition = 0;
  @Output() selectAllChange = new EventEmitter<boolean>();

  @Output() columnResized = new EventEmitter<{ width: number; column: VirtualGridColumn }>();
  @Output() columnResizeEnd = new EventEmitter<{ width: number; column: VirtualGridColumn }>();
  @Output() columnReordered = new EventEmitter<{
    columns: Array<VirtualGridColumn>;
    previousIndex: number;
    currentIndex: number;
  }>();
  @Output() contextMenuOperationSelected = new EventEmitter<{ menuOperation: ContextMenuOperation; data: any }>();
  @Output() sortCriteriaChange = new EventEmitter<SortCriteria[]>();
  @Output() horizontalScrollPositionChange = new EventEmitter<number>();

  @Input() rtl = false;
  get rtlSignal(): -1 | 1 {
    return this.rtl ? -1 : 1;
  }

  @ViewChild('innerWorkListHeader') gridHeader: ElementRef;
  @ViewChildren('cells') cells: QueryList<ElementRef>;
  @ViewChild('gridHeaderContextMenu') gridHeaderContextMenu: TemplateRef<any>;
  @ViewChild('unfrozen') unfrozenElement: ElementRef;

  /**
   * If specified, provides the filter criteria for the grid.
   */
  @Input()
  filterCriteria?: FilterCriteria[];

  /**
   * Notifies the host of a change to the filter criteria.
   */
  @Output()
  filterCriteriaChange: EventEmitter<FilterCriteria[]> = new EventEmitter<FilterCriteria[]>();

  private _oldWidth: number;
  private startX = 0;
  private mouseMoveEvent: () => void;
  private mouseUpEvent: () => void;

  private hideOperation: ContextMenuOperation = {
    id: 'hide',
    label: 'vg.hideColumn'
  };
  private freezeOperation: ContextMenuOperation = {
    id: 'freeze',
    label: 'vg.freezeColumn',
    isHidden: false
  };
  private unfreezeOperation: ContextMenuOperation = {
    id: 'unfreeze',
    label: 'vg.unfreezeColumn',
    isHidden: true
  };
  private groupByOperation: ContextMenuOperation = {
    id: 'groupBy',
    label: 'vg.groupByColumn',
    isHidden: false
  };
  private ungroupByOperation: ContextMenuOperation = {
    id: 'ungroupBy',
    label: 'vg.ungroupByColumn',
    isHidden: true
  };
  contextMenuOperations: Array<ContextMenuOperation> = [
    this.hideOperation,
    this.freezeOperation,
    this.unfreezeOperation,
    this.groupByOperation,
    this.ungroupByOperation
  ];

  constructor(
    @Inject(DOCUMENT) private document: any,
    private viewContainerRef: ViewContainerRef,
    private contextMenuService: ContextMenuService,
    public renderer: Renderer2,
    changeDetectorRef: ChangeDetectorRef
  ) {
    super(document.defaultView, changeDetectorRef);
  }

  detectChanges() {
    this.cd.detectChanges();
  }

  resizeStart = (event: MouseEvent, column: VirtualGridColumn) => {
    const columns = (this.layout || []).filter(c => !c.isHidden);
    const index = columns.indexOf(column);

    event.stopPropagation();
    this.startX = event.pageX;
    this._oldWidth = this.cells.toArray()[index].nativeElement.offsetWidth;
    this.mouseMove(column);

    if (this.activeFilterColumn) {
      this.activeFilterColumn = undefined;
      this.$detectChanges();
    }
  };

  isDraging = false;

  drop = (event: CdkDragDrop<any[]>) => {
    const cells = this.cells.toArray();
    const pIndex = +cells[event.previousIndex].nativeElement.id.split('header--cel-')[1];
    const cIndex = +cells[event.currentIndex].nativeElement.id.split('header--cel-')[1];

    moveItemInArray(this.layout, pIndex, cIndex);
    if (event.previousIndex !== event.currentIndex) {
      this.columnReordered.emit({
        columns: this.layout,
        previousIndex: event.previousIndex,
        currentIndex: event.currentIndex
      });
    }
  };

  dragStarted() {
    this.isDraging = true;

    if (this.activeFilterColumn) {
      this.activeFilterColumn = undefined;
      this.$detectChanges();
    }
  }

  openContextMenu(e: MouseEvent, column: VirtualGridColumn) {
    if (!this.disableHeaderContextMenu) {
      this.freezeOperation.isHidden =
        (column.isPinnable !== undefined && !column.isPinnable) || column === this.frozenColumn;
      this.unfreezeOperation.isHidden =
        (column.isPinnable !== undefined && !column.isPinnable) || column !== this.frozenColumn;

      this.groupByOperation.isHidden =
        (column.isGroupable !== undefined && !column.isGroupable) || !!column.isGroupedBy;
      this.ungroupByOperation.isHidden =
        !column.isGroupedBy || (column.isGroupable !== undefined && !column.isGroupable);

      this.contextMenuService
        .openContextMenu(
          this.document.children[0].getBoundingClientRect(), // using root element as boundary
          e,
          this.gridHeaderContextMenu,
          this.viewContainerRef,
          column
        )
        .subscribe((contextData: any) => {
          if (contextData && contextData.menuOperation) {
            switch (contextData.menuOperation.id) {
              case 'groupBy':
                contextData.data.isGroupedBy = true;
                break;
              case 'ungroupBy':
                contextData.data.isGroupedBy = false;
                break;
              case 'freeze':
                this.frozenColumn = contextData.data;
                this.frozenColumnChange.emit(this.frozenColumn);
                break;
              case 'unfreeze':
                this.frozenColumn = undefined;
                this.frozenColumnChange.emit(this.frozenColumn);
                break;
              case 'hide':
                contextData.data.isHidden = true;
                break;
            }
            this.contextMenuOperationSelected.emit(contextData);
          }
        });
    }
    this.$detectChanges(10);
  }

  contextMenuSelected = (menuOperation: ContextMenuOperation) => {
    this.contextMenuService.close(menuOperation);
  };

  private mouseMove(column: VirtualGridColumn) {
    this.mouseMoveEvent && this.mouseMoveEvent();
    this.mouseMoveEvent = this.renderer.listen(this.gridHeader.nativeElement, 'mousemove', event => {
      event.preventDefault();
      if (event.buttons) {
        this.resizingEvent((event.pageX - this.startX) * this.rtlSignal, column);
      }
    });
    this.mouseUpEvent && this.mouseUpEvent();
    this.mouseUpEvent = this.renderer.listen(this.gridHeader.nativeElement, 'mouseup', () => {
      this.resizeEnd(column);
    });
  }

  private resizingEvent(distanceX: number, column: VirtualGridColumn) {
    const minWidth = column.minWidth || 50;
    const maxWidth = column.maxWidth > minWidth && column.maxWidth;

    if (column.isResizable !== undefined && !column.isResizable) return;

    let newWidth = this._oldWidth + distanceX;

    if (newWidth < minWidth) {
      newWidth = minWidth;
    }

    if (maxWidth && newWidth > maxWidth) {
      newWidth = maxWidth;
    }

    column.width = newWidth;

    if (this._oldWidth !== newWidth) {
      this.columnResized.emit({
        width: column.width,
        column
      });
    }
  }

  private resizeEnd(column: VirtualGridColumn) {
    this.columnResizeEnd.emit({ width: column.width, column });

    this._oldWidth = null;
    this.startX = 0;
    this.mouseMoveEvent && this.mouseMoveEvent();
    this.mouseUpEvent && this.mouseUpEvent();
  }

  /**
   * Determines if the given column is sortable or not.
   */
  isColumnSortable(column: VirtualGridColumn): boolean {
    return !column.multiFieldItems && !!column.defaultSort;
  }

  isColumnSorted(column: VirtualGridColumn, asc: boolean): boolean {
    const sc: SortCriteria = this.sortCriteria?.find(value => column.id === value.column.id);
    if (sc) {
      return sc.isOrderAscending === asc;
    }

    return false;
  }

  onToggleColumnSort(column: VirtualGridColumn): void {
    const sc: SortCriteria = this.sortCriteria?.find(value => column.id === value.column.id);
    if (sc) {
      // Toggle it if we can find it...
      sc.isOrderAscending = !sc.isOrderAscending;
    } else {
      // Or change to this column if we can't.
      this.sortCriteria.push({
        column: column,
        isOrderAscending: column.defaultSort.toLowerCase() == 'ascending'
      });
    }
    this.sortCriteriaChange.emit(this.sortCriteria);

    this.activeFilterColumn = undefined;
    this.$detectChanges();
  }

  /**
   * Returns column field indicator. Pure function.
   */
  getSortedFieldIndicator(value: any, col: VirtualGridColumn, sc: SortCriteria[]): string {
    if (sc?.length && sc[0]?.column.id === col.id && sc[0].column.multiFieldItems && sc[0].column.defaultSort) {
      return sc[0].sortField.icon;
    }
  }

  itemSortSelectionChange(event: { column: VirtualGridColumn; ascending: boolean }): void {
    if (event && event.column) {
      const sc: SortCriteria = this.sortCriteria?.find(value => event.column.id === value.column.id);
      if (sc) {
        // Toggle it if we can find it...
        sc.isOrderAscending = event.ascending;
      } else {
        // Or change to this column if we can't.
        this.sortCriteria.push({
          column: event.column,
          isOrderAscending: event.ascending
        });
      }
      this.sortCriteriaChange.emit(this.sortCriteria);
    }
  }

  onMoreOptionsClick(column: VirtualGridColumn): void {
    if (this.isColumnFilterable(column) || column.sortTemplate) {
      this.setActiveFilterColumn(column, true);
    }
  }

  onScroll(): void {
    this.horizontalScrollPosition = this.unfrozenElement.nativeElement.scrollLeft * this.rtlSignal;
    this.horizontalScrollPositionChange.emit(this.horizontalScrollPosition);
  }

  scrollToCell(cell: ElementRef<any>) {
    if (!cell) return;
    const parent = (cell.nativeElement as HTMLElement).parentElement;
    if (parent.scrollWidth > parent.clientWidth) {
      (cell.nativeElement as HTMLElement).scrollIntoView(false);
    }
  }

  activateFilter(column: VirtualGridColumn, next?: boolean): void {
    let index = this.layout.indexOf(column);

    const activateFunction = () => {
      // Get out of the current callstack to activate.
      this.window.setTimeout((): void => {
        const column = this.layout[index];
        const cell = this.cells.toArray().find(cell => cell.nativeElement.id === `header--cel-${index}`);
        this.scrollToCell(cell);
        this.setActiveFilterColumn(column, true);
      }, 50);
    };
    // Undefined means don't activate next or previous filterable column.
    if (next !== undefined) {
      if (next) {
        // Search for the next filterable column to activate.
        for (++index; index < this.layout.length; ++index) {
          if (this.isColumnFilterable(this.layout[index])) {
            activateFunction();
            break;
          }
        }
      } else {
        // Search fo the previous filterable column to activate.
        for (--index; index >= 0; --index) {
          if (this.isColumnFilterable(this.layout[index])) {
            activateFunction();
            break;
          }
        }
      }
    }
  }

  /**
   * Gets the filter data for the given column by inspecting the filter criteria.
   */
  getFilterData(column: VirtualGridColumn): any {
    const filterCriteria = this.filterCriteria?.find(value => value.column.id === column.id);
    return filterCriteria?.filterData;
  }

  /**
   * Determines if the filter of the given column is active and displayed to the user.
   */
  isActiveFilterColumn(column: VirtualGridColumn): boolean {
    return !this.isDraging && !!this.activeFilterColumn && this.activeFilterColumn.id === column.id;
  }

  /**
   * Determines if the given column is filterable or not.
   */
  isColumnFilterable(column: VirtualGridColumn): boolean {
    return !!column.filterConfig && !column.isHidden;
  }

  /**
   * Determines if the given column is currently filtered.
   */
  isColumnFiltered(column: VirtualGridColumn): boolean {
    return !!column.filterConfig && !!this.getFilterData(column);
  }

  /**
   * Creates a new filter criteria array based on the change in the current column filter and notifies
   * the host.
   */
  // Filter data could be anything.
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onFilterDataChange(column: VirtualGridColumn, filterData: any): void {
    // Copy the array.
    const filterCriteria = [...(this.filterCriteria || [])];

    // Find the index (if any) of the existing filter.
    const idx = filterCriteria.findIndex(value => value.column.id === column.id);

    if (filterData) {
      if (idx === -1) {
        // Add to the end.
        filterCriteria.push({
          column: column,
          filterData: filterData
        });
      } else {
        // Update the filter data at the index.
        filterCriteria[idx].filterData = filterData;
      }
    } else {
      // removed
      if (idx === -1) {
        // No op.
      } else {
        filterCriteria.splice(idx, 1);
      }
    }

    this.filterCriteria = filterCriteria;
    this.filterCriteriaChange.emit(filterCriteria);
  }

  /**
   * Handles a click on the column header label and sorts the column by the default for that column.
   */
  onHeaderLabelClick(column: VirtualGridColumn): void {
    if (this.isColumnSortable(column)) {
      // If column is not sorted already,
      this.onToggleColumnSort(column);
    }
  }

  /**
   * Toggles the active filter state of the given column.
   */
  setActiveFilterColumn(column: VirtualGridColumn, active: boolean): void {
    if (active && !this.isActiveFilterColumn(column)) {
      this.activeFilterColumn = column;
    } else if (this.isActiveFilterColumn(column)) {
      this.activeFilterColumn = undefined;
    }
    // There is injected code in the html that relies on the active filter column.  We need to manually
    // detct the changes here as this component now uses the OnPush strategy.
    this.$detectChanges();
  }

  /**
   * Variable that tracks the column whose filter is currently active and displayed to the user.
   */
  private activeFilterColumn: VirtualGridColumn;
}
